/**
 * TEMP — Set `false` before shipping. When `true`, the gallery lists every product image
 * (no Apex combination filtering); the slide matching the current Apex selection still
 * moves to index 0 so the primary image updates.
 */
const BODY_MEDIA_DISABLE_APEX_FILTER = false

const COVERAGE_FRACTION = 0.6
/** Display is ~1/3 of the viewport; 300w covers ~2× for typical 36vw float on mobile. */
const FLOATING_THUMB_PX = 300
/** Matches Tailwind `max-lg` (1023px): floating image only below the `lg` breakpoint. */
const FLOATING_IMAGE_MQ = '(max-width: 1023px)'
/** Hide the floater this many px before the section bottom would align with the floater bottom (viewport Y). */
const EARLY_HIDE_BEFORE_BOTTOM_CROSS_PX = 28

function mediaRootSelector(sectionId) {
  return `[data-product-media-body="${sectionId}"]`
}

/** @param {ParentNode} [root] */
function getSearchRoot(root) {
  return root && typeof root.querySelectorAll === 'function' ? root : document
}

function getBodyMediaState(sectionId) {
  const mediaEl = document.querySelector(mediaRootSelector(sectionId))
  const splide = mediaEl?._bodyProductMediaSplide
  const slides = splide?.Components?.Elements?.slides
  if (!splide || !Array.isArray(slides) || !slides.length) return null
  return { mediaEl, splide, slides }
}

function initFloatingProductImage(el) {
  if (el.dataset.floatingImageInit === 'true') return

  const sectionId = el.getAttribute('data-floating-product-image-section')
  if (!sectionId) return

  const productSection =
    document.getElementById(`MainProduct-${sectionId}`) ||
    document.querySelector(`[data-main-product-body="${sectionId}"]`)
  if (!productSection) return

  el.dataset.floatingImageInit = 'true'

  const belowLgMq = window.matchMedia(FLOATING_IMAGE_MQ)
  let ticking = false
  let visible = false

  const threshold = () => window.innerWidth * COVERAGE_FRACTION

  const setVisible = (next) => {
    if (next === visible) return
    visible = next
    el.dataset.visible = next ? 'true' : 'false'
    el.setAttribute('aria-hidden', next ? 'false' : 'true')
    el.tabIndex = next ? 0 : -1
  }

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  const handleActivate = (e) => {
    if (el.getAttribute('data-visible') !== 'true') return
    e.preventDefault()
    scrollToTop()
  }

  el.addEventListener('click', handleActivate)
  el.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleActivate(e)
  })

  const update = () => {
    if (!belowLgMq.matches) {
      setVisible(false)
      return
    }
    const scrollY = window.scrollY || window.pageYOffset || 0
    const pastCoverage = scrollY >= threshold()

    const sectionRect = productSection.getBoundingClientRect()
    const floatRect = el.getBoundingClientRect()
    const vh = window.innerHeight
    const sectionOverlapsViewport = sectionRect.bottom > 0 && sectionRect.top < vh
    const nearFloaterBottom = sectionRect.bottom < floatRect.bottom + EARLY_HIDE_BEFORE_BOTTOM_CROSS_PX

    setVisible(pastCoverage && sectionOverlapsViewport && !nearFloaterBottom)
  }

  const onScroll = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      ticking = false
      update()
    })
  }

  update()
  window.addEventListener('scroll', onScroll, { passive: true })
  window.addEventListener('resize', update)
  if (typeof belowLgMq.addEventListener === 'function') {
    belowLgMq.addEventListener('change', update)
  } else if (typeof belowLgMq.addListener === 'function') {
    belowLgMq.addListener(update)
  }

  scheduleFloatingThumbPreload(sectionId)
}

// --- Apex product options → featured image (Splide + floating thumbnail) ---

/**
 * Filenames: `<anything>__<model>-<quantity>-<stand>.<ext>` — match only the part after `__` (no extension);
 * Shopify may append `_…` after that segment.
 */
function extractApexFileKeyFromSrc(src) {
  if (!src) return null
  let pathname
  try {
    pathname = new URL(src, window.location.href).pathname
  } catch (e) {
    return null
  }
  const file = decodeURIComponent(pathname.split('/').pop() || '')
  const idx = file.indexOf('__')
  if (idx === -1) return null
  let rest = file.slice(idx + 2)
  const lastDot = rest.lastIndexOf('.')
  if (lastDot > 0) rest = rest.slice(0, lastDot)
  return rest || null
}

function fileKeyMatches(key, desiredKey) {
  if (!key || !desiredKey) return false
  if (key === desiredKey) return true
  return key.split('_')[0] === desiredKey
}

/**
 * All valid `model-quantity-stand` keys from visible Apex inputs, filtered by
 * `APEX_STANDS_BY_QUANTITY` (same rules as `updateApexStandAvailability`).
 * @param {ParentNode} apexRoot
 * @returns {string[]}
 */
function enumerateValidApexKeys(apexRoot) {
  if (!apexRoot) return []
  const models = [...apexRoot.querySelectorAll('input[data-apex-input="model"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const quantities = [...apexRoot.querySelectorAll('input[data-apex-input="quantity"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const stands = [...apexRoot.querySelectorAll('input[data-apex-input="stand"]')].filter(
    (e) => e instanceof HTMLInputElement
  )
  const keys = []
  for (const m of models) {
    for (const q of quantities) {
      const allowed = APEX_STANDS_BY_QUANTITY[q.value]
      if (!allowed) continue
      for (const s of stands) {
        if (!allowed.has(s.value)) continue
        keys.push([m.value, q.value, s.value].join('-'))
      }
    }
  }
  return keys
}

function getApexOptionValues(apexRoot) {
  const model = apexRoot.querySelector('[data-apex-input="model"]:checked')?.value
  const quantity = apexRoot.querySelector('[data-apex-input="quantity"]:checked')?.value
  const stand = apexRoot.querySelector('[data-apex-input="stand"]:checked')?.value
  if (model == null || quantity == null || stand == null) return null
  return { model, quantity, stand, key: [model, quantity, stand].join('-') }
}

/** Mount options available per panel quantity (door = `bare`). */
const APEX_STANDS_BY_QUANTITY = {
  '1p': new Set(['bare', 'floor', 'universal']),
  '2p': new Set(['bare', 'universal', 'motorized']),
  '4p': new Set(['bare', 'motorized']),
}

const APEX_OPTION_STEPS = ['model', 'quantity', 'stand']

const APEX_URL_PARAM_VARIANT = 'variant'
const APEX_URL_PARAM_QUANTITY = 'quantity'
const APEX_URL_PARAM_STAND = 'stand'

/**
 * @returns {boolean} True when the URL encodes Apex state (e.g. shared deep link from body PDP).
 * When true, we mark desktop accordion steps as selected/open so options don't look "unpicked"
 * until the user touches them. When false, the default `setupApexDesktopAccordion` stays.
 */
function hasApexUrlParams() {
  try {
    const sp = new URLSearchParams(window.location.search)
    return (
      sp.has(APEX_URL_PARAM_VARIANT) ||
      sp.has(APEX_URL_PARAM_QUANTITY) ||
      sp.has(APEX_URL_PARAM_STAND)
    )
  } catch {
    return false
  }
}

/**
 * After radios reflect URL/defaults, shows each fieldset on xl as selected+expanded
 * (matches post-interaction `advanceApexOptionStep` appearance).
 * @param {ParentNode} apexRoot
 */
function syncApexDesktopUIFromUrl(apexRoot) {
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    !window.matchMedia('(min-width: 1280px)').matches
  ) {
    return
  }
  for (const step of APEX_OPTION_STEPS) {
    const stepEl = getApexStep(apexRoot, step)
    if (!(stepEl instanceof HTMLElement)) continue
    const $input = apexRoot.querySelector(`[data-apex-input="${step}"]:checked`)
    if (!$input) continue
    setApexStepSelected(apexRoot, step, true)
    setApexStepOpen(stepEl, true)
  }
}

function updateApexStandAvailability(apexRoot) {
  const qty = apexRoot.querySelector('[data-apex-input="quantity"]:checked')?.value
  if (!qty) return
  const allowed = APEX_STANDS_BY_QUANTITY[qty]
  if (!allowed) return

  const standInputs = apexRoot.querySelectorAll('input[data-apex-input="stand"]')
  standInputs.forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.disabled = !allowed.has(input.value)
    }
  })

  const selected = apexRoot.querySelector('input[data-apex-input="stand"]:checked')
  if (selected instanceof HTMLInputElement && !selected.disabled && allowed.has(selected.value)) {
    return
  }

  const enabled = Array.from(standInputs).filter(
    (el) => el instanceof HTMLInputElement && !el.disabled && allowed.has(el.value)
  )
  if (!enabled.length) return
  const next = enabled.find((el) => el.value === 'bare') || enabled[0]
  next.checked = true
}

/**
 * "Popular" badge moves to floor (1p), universal (2p), or motorized (4p).
 * @param {ParentNode} apexRoot
 */
function updateApexPopularBadge(apexRoot) {
  const qty = apexRoot.querySelector('input[data-apex-input="quantity"]:checked')?.value
  if (qty == null) return
  apexRoot.querySelectorAll('[data-apex-stand-popular-badge]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return
    const forQty = el.dataset.apexPopularQuantity
    if (forQty == null) return
    el.classList.toggle('hidden', forQty !== qty)
  })
}

/**
 * Stand thumbnails ship as paired white/black art (Pro vs Elite image edges). Shows the
 * `img[data-apex-option-content-image="<model value>"]` that matches `model`; hides the other.
 * @param {ParentNode} apexRoot
 */
function syncApexStandThumbnailImagesForModel(apexRoot) {
  const model = apexRoot.querySelector('[data-apex-input="model"]:checked')?.value
  if (model !== '1500' && model !== '2160') return

  apexRoot.querySelectorAll('img[data-apex-option-content-image]').forEach((node) => {
    if (!(node instanceof HTMLImageElement)) return
    const variant = node.getAttribute('data-apex-option-content-image')
    if (!variant || (variant !== '1500' && variant !== '2160')) return
    node.classList.toggle('hidden', variant !== model)
  })
}

function getApexStep(apexRoot, step) {
  return apexRoot.querySelector(`[data-apex-group="${step}"]`)
}

function setApexStepOpen(stepEl, isOpen) {
  if (!(stepEl instanceof HTMLElement)) return
  stepEl.dataset.apexOpen = isOpen ? 'true' : 'false'
  const trigger = stepEl.querySelector('[data-apex-accordion-trigger]')
  if (trigger instanceof HTMLElement) {
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false')
  }
}

function setApexStepSelected(apexRoot, step, isSelected = true) {
  const stepEl = getApexStep(apexRoot, step)
  if (stepEl instanceof HTMLElement) {
    stepEl.dataset.apexSelected = isSelected ? 'true' : 'false'
  }
}

function openApexStep(apexRoot, step) {
  setApexStepOpen(getApexStep(apexRoot, step), true)
}

function setupApexDesktopAccordion(apexRoot) {
  APEX_OPTION_STEPS.forEach((step) => {
    const stepEl = getApexStep(apexRoot, step)
    if (!(stepEl instanceof HTMLElement)) return
    const isModel = step === 'model'
    setApexStepOpen(stepEl, isModel)
    stepEl.dataset.apexSelected = 'false'

    const trigger = stepEl.querySelector('[data-apex-accordion-trigger]')
    if (trigger instanceof HTMLElement && trigger.dataset.apexAccordionTriggerInit !== 'true') {
      trigger.dataset.apexAccordionTriggerInit = 'true'
      trigger.addEventListener('click', () => {
        setApexStepOpen(stepEl, stepEl.dataset.apexOpen !== 'true')
      })
    }
  })
}

function advanceApexOptionStep(apexRoot, step) {
  setApexStepSelected(apexRoot, step)
  if (step === 'model') {
    openApexStep(apexRoot, 'quantity')
  } else if (step === 'quantity') {
    openApexStep(apexRoot, 'stand')
  }
}

function getSlideImageLikeSrc(slideEl) {
  const img = slideEl.querySelector('img')
  if (img) return img.currentSrc || img.src || ''
  return slideEl.querySelector('video')?.poster || ''
}

/**
 * Shrink a Shopify CDN image URL to the floating thumb size (avoid loading full gallery `width=`).
 * @param {string} src
 * @returns {string}
 */
function shopifyImageUrlForFloatingThumb(src) {
  if (!src) return src
  try {
    const u = new URL(src, window.location.href)
    u.searchParams.set('width', String(FLOATING_THUMB_PX))
    u.searchParams.set('height', String(FLOATING_THUMB_PX))
    u.searchParams.set('crop', 'center')
    return u.toString()
  } catch (e) {
    return src
  }
}

/**
 * `splide.Components.Elements.slides[i]` is the slide root `Element` in this theme
 * (see product-media-body). Some Splide builds use `{ slide: Element }` instead.
 */
function splideSlideRoot(item) {
  if (!item) return null
  if (item.nodeType === 1) return item
  return item.slide || null
}

function findSlideIndexForApexKey(slides, desiredKey) {
  for (let i = 0; i < slides.length; i++) {
    const el = splideSlideRoot(slides[i])
    if (!el) continue
    const fileKey = extractApexFileKeyFromSrc(getSlideImageLikeSrc(el))
    if (fileKeyMatches(fileKey, desiredKey)) return i
  }
  return -1
}

function getApexKeyForSlide(slideEl) {
  return extractApexFileKeyFromSrc(getSlideImageLikeSrc(slideEl))
}

function rememberOriginalBodyMedia(mediaEl, slides) {
  if (mediaEl._bodyProductOriginalSlides) return
  mediaEl._bodyProductOriginalSlides = slides.map((slide) => splideSlideRoot(slide)).filter(Boolean)
  const thumbsList = mediaEl.querySelector('[data-product-media-body-thumbs-list]')
  mediaEl._bodyProductOriginalThumbs = thumbsList
    ? [...thumbsList.querySelectorAll(':scope > .splide__slide')]
    : Array.from(mediaEl.querySelectorAll('[data-product-media-body-thumb]'))
}

function rebuildBodyMediaOrder(state, selectedKey, validKeys, placement = 'first') {
  const { mediaEl, splide, slides } = state
  const list = mediaEl.querySelector('[data-product-media-body-main] .splide__list')
  const thumbsList = mediaEl.querySelector('[data-product-media-body-thumbs-list]')
  if (!list) return null

  rememberOriginalBodyMedia(mediaEl, slides)

  const originalSlides = mediaEl._bodyProductOriginalSlides || []
  const selectedSlide = originalSlides.find((slide) => fileKeyMatches(getApexKeyForSlide(slide), selectedKey))
  if (!selectedSlide) return null

  const seeItSlide =
    placement === 'after-video'
      ? originalSlides.find((slide) => slide.hasAttribute('data-see-it-in-action-main-slide'))
      : null
  const leadingSlides = seeItSlide ? [seeItSlide, selectedSlide] : [selectedSlide]

  let orderedSlides
  if (BODY_MEDIA_DISABLE_APEX_FILTER) {
    orderedSlides = [...leadingSlides, ...originalSlides.filter((slide) => !leadingSlides.includes(slide))]
  } else {
    const apexKeys = new Set(validKeys)
    orderedSlides = [
      ...leadingSlides,
      ...originalSlides.filter((slide) => {
        if (leadingSlides.includes(slide)) return false
        const apexKey = getApexKeyForSlide(slide)
        return !apexKey || !apexKeys.has(apexKey.split('_')[0])
      }),
    ]
  }

  list.replaceChildren(...orderedSlides)

  if (thumbsList && mediaEl._bodyProductOriginalThumbs) {
    const originalThumbs = mediaEl._bodyProductOriginalThumbs

    const orderedThumbs = (() => {
      const useStableIndex =
        orderedSlides.every((el) => el && el.hasAttribute && el.hasAttribute('data-body-slide-index')) &&
        originalThumbs.every((el) => el && el.hasAttribute && el.hasAttribute('data-body-slide-index'))

      if (useStableIndex) {
        return orderedSlides
          .map((slideEl) => {
            const stable = slideEl.getAttribute('data-body-slide-index')
            if (stable == null) return null
            return originalThumbs.find((thumbSlide) => thumbSlide.getAttribute('data-body-slide-index') === stable)
          })
          .filter(Boolean)
      }

      return orderedSlides
        .map((slide) => originalSlides.indexOf(slide))
        .map((originalIndex) => originalThumbs[originalIndex])
        .filter(Boolean)
    })()

    orderedThumbs.forEach((thumbSlide, index) => {
      const btn = thumbSlide.querySelector('[data-product-media-body-thumb]')
      if (btn) btn.dataset.slideIndex = String(index)
    })
    if (orderedThumbs.length === orderedSlides.length) {
      thumbsList.replaceChildren(...orderedThumbs)
    }
  }

  splide.refresh()
  try {
    mediaEl._bodyProductThumbSplide?.refresh?.()
  } catch (e) {}
  return selectedSlide
}

/**
 * 300×300 URLs for every floating thumb that might be shown. Uses valid Apex
 * combinations when `[data-apex-product-options]` exists; if none match media
 * filenames, falls back to all gallery slides.
 * @param {string} sectionId
 * @param {ParentNode | null} apexRoot
 * @returns {string[]}
 */
function collectFloatingThumbUrlsToPreload(sectionId, apexRoot) {
  const state = getBodyMediaState(sectionId)
  if (!state) return []
  const { slides } = state
  const urls = new Set()

  if (apexRoot) {
    for (const key of enumerateValidApexKeys(apexRoot)) {
      const i = findSlideIndexForApexKey(slides, key)
      if (i < 0) continue
      const slideEl = splideSlideRoot(slides[i])
      if (!slideEl) continue
      const raw = getSlideImageLikeSrc(slideEl)
      if (raw) urls.add(shopifyImageUrlForFloatingThumb(raw))
    }
  }

  if (urls.size === 0) {
    for (let i = 0; i < slides.length; i++) {
      const slideEl = splideSlideRoot(slides[i])
      if (!slideEl) continue
      const raw = getSlideImageLikeSrc(slideEl)
      if (raw) urls.add(shopifyImageUrlForFloatingThumb(raw))
    }
  }
  return [...urls]
}

function runFloatingThumbPreload(urls) {
  for (const url of urls) {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
  }
}

/**
 * After Splide is ready, preloads 300×300 hover-thumb assets (idle time, non-blocking).
 * @param {string} sectionId
 */
function scheduleFloatingThumbPreload(sectionId) {
  const floatEl = document.querySelector(
    `[data-floating-product-image][data-floating-product-image-section="${sectionId}"]`
  )
  if (!floatEl || floatEl.dataset.floatingThumbsPreloaded === 'true') return

  const apexRoot = document.querySelector(
    `[data-apex-product-options][data-section-id="${sectionId}"]`
  )

  whenSplideReady(sectionId, () => {
    if (floatEl.dataset.floatingThumbsPreloaded === 'true') return
    const urls = collectFloatingThumbUrlsToPreload(sectionId, apexRoot)
    if (!urls.length) return
    floatEl.dataset.floatingThumbsPreloaded = 'true'
    const kick = () => runFloatingThumbPreload(urls)
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(kick, { timeout: 4000 })
    } else {
      setTimeout(kick, 0)
    }
  })
}

function updateFloatingImageFromSlide(slideEl, floatContainer) {
  const floatImg = floatContainer?.querySelector('img')
  if (!floatImg) return
  const src = getSlideImageLikeSrc(slideEl)
  if (!src) return
  const sized = shopifyImageUrlForFloatingThumb(src)
  floatImg.removeAttribute('srcset')
  floatImg.width = FLOATING_THUMB_PX
  floatImg.height = FLOATING_THUMB_PX
  floatImg.src = sized
}

function syncApexToMedia(apexRoot, sectionId, placement = 'first') {
  const values = getApexOptionValues(apexRoot)
  if (!values) return

  const state = getBodyMediaState(sectionId)
  if (!state) return

  const { splide } = state
  const selectedSlide = rebuildBodyMediaOrder(state, values.key, enumerateValidApexKeys(apexRoot), placement)
  if (!selectedSlide) return

  try {
    splide.go(0)
  } catch (e) {
    return
  }

  const floatContainer = document.querySelector(
    `[data-floating-product-image][data-floating-product-image-section="${sectionId}"]`
  )
  if (floatContainer) updateFloatingImageFromSlide(selectedSlide, floatContainer)
}

function whenSplideReady(sectionId, callback, attempt = 0) {
  const mediaEl = document.querySelector(mediaRootSelector(sectionId))
  if (!mediaEl) return
  if (mediaEl._bodyProductMediaSplide) {
    callback()
    return
  }
  if (attempt > 50) return
  requestAnimationFrame(() => whenSplideReady(sectionId, callback, attempt + 1))
}

function initApexProductOptions(apexRoot) {
  if (apexRoot.dataset.apexProductOptionsInit === 'true') return
  const sectionId = apexRoot.getAttribute('data-section-id')
  if (!sectionId) return

  apexRoot.dataset.apexProductOptionsInit = 'true'

  const syncImage = (placement = 'first') => whenSplideReady(sectionId, () => syncApexToMedia(apexRoot, sectionId, placement))

  const onApexInputChange = () => {
    updateApexStandAvailability(apexRoot)
    updateApexPopularBadge(apexRoot)
    syncApexStandThumbnailImagesForModel(apexRoot)
    syncImage()
  }

  setupApexDesktopAccordion(apexRoot)

  apexRoot.addEventListener('change', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.hasAttribute('data-apex-input')) {
      advanceApexOptionStep(apexRoot, e.target.dataset.apexInput)
      onApexInputChange()
    }
  })

  apexRoot.addEventListener('click', (e) => {
    if (e.target instanceof HTMLInputElement && e.target.hasAttribute('data-apex-input')) {
      advanceApexOptionStep(apexRoot, e.target.dataset.apexInput)
    }
  })

  updateApexStandAvailability(apexRoot)
  updateApexPopularBadge(apexRoot)
  if (hasApexUrlParams()) {
    syncApexDesktopUIFromUrl(apexRoot)
  }
  syncApexStandThumbnailImagesForModel(apexRoot)
  syncImage('after-video')
}

function initApexProductOptionsInScope(root) {
  getSearchRoot(root)
    .querySelectorAll('[data-apex-product-options]')
    .forEach((node) => {
      if (node instanceof HTMLElement) initApexProductOptions(node)
    })
}

function initFloatingInScope(root) {
  getSearchRoot(root)
    .querySelectorAll('[data-floating-product-image][data-floating-product-image-section]')
    .forEach((node) => {
      if (node instanceof HTMLElement) initFloatingProductImage(node)
    })
}

function runInit() {
  initFloatingInScope(document)
  initApexProductOptionsInScope(document)
}

if (!window.__mainProductBodyJsBound) {
  window.__mainProductBodyJsBound = true
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInit)
  } else {
    runInit()
  }
  document.addEventListener('shopify:section:load', (e) => {
    if (e?.target instanceof HTMLElement) {
      initFloatingInScope(e.target)
      initApexProductOptionsInScope(e.target)
    }
  })
} else {
  runInit()
}
